(ns practitest.frontend.reports.events
  (:require
   [clojure.set                      :refer [difference]]
   [re-frame.core                    :as re-frame]
   [day8.re-frame.http-fx]
   [ajax.core                        :as ajax]
   [practitest.frontend.common-old-fe.logging          :as log]
   [practitest.frontend.reports.utils           :as ru]
   [practitest.frontend.routing.router          :as rr]
   [practitest.frontend.events :as events]))

;; ===========================================================================
;; interceptors

(def common-interceptors
  [re-frame/trim-v])

;; ===========================================================================
;; constants

(def ^:const request-timeout 60000)
(def ^:const pulling-timeout 300)
(def ^:const rd-pulling-timeout 30000)
(def ^:const modal-size :extra-large)

;; ===========================================================================
;; utils

(def scopes [:reports])

(defn transform-instance [instance]
  ;; 'files' is an array of s3 urls
  ;; we need to transform it to a list of tuples, so that we'll know how to display it
  (-> instance
      (assoc :progress (get instance :progress 1))
      (update :files
              (fn [urls]
                (merge {:excel {:label "Prepare Excel"
                                :url   :prepare}
                        :html  {:label "Prepare HTML"
                                :url   :disabled}
                        :pdf   {:label "Prepare PDF"
                                :url   :disabled}}
                       (->> urls
                            (map (fn [[ts url]]
                                   (let [[_ ext] (re-find #"\.(.+)$" url)]
                                     (condp = ext
                                       "xlsx" [:excel {:label "Download Excel"
                                                       :url   url
                                                       :ts    ts}]
                                       "html" [:html  {:label "Download HTML"
                                                       :url   url
                                                       :ts    ts}]
                                       "pdf"  [:pdf   {:label "Download PDF"
                                                       :url   url
                                                       :ts    ts}]))))
                            (into {})))))))

(defn filter->filter-name [filters rd]
      (let [filter-id (:filter-by rd)
            {:keys [entity generate-by testset-id]} rd
            filter-names (->> (get filters (keyword entity))
                              (map (fn [[text id _]]
                                       [id text]))
                              (into {}))]
           (if (and (= generate-by "by_testset_id") testset-id)
             (str "Test Set ID: " testset-id)
             (get filter-names filter-id))))

;; ===========================================================================
;; event handlers

(re-frame/reg-event-db
  :reports/init-complete
  (fn [db _]
    (assoc db :init-complete? true)))

(re-frame/reg-event-db
  :reports/set-default-filter-fields
  (fn [db [_ options]]
    (assoc db :default-filter-fields (:default-fields options))))

(re-frame/reg-event-fx
  :reports.ws/message
  [common-interceptors]
  (fn [{:keys [db]} [msg]]
    (log/info :reports.ws/message msg)
    (let [[updated-db dispatch]
          (if (:res msg)
            (do
              (log/info :reports.ws/message (:res msg))
              [db nil])
            (case (:action msg)
              "rd-destroyed"
              [db
               (when (contains? (:report-definitions db) (:id msg))
                 [[:reports/report-definition-deleted msg]])]

              "rd-created"
              [db nil]

              "instance-destroyed"
              [(update-in db [:report-definitions (:id msg) :instances]
                          (fn [instances]
                            (remove #(= (:key msg) (:key %)) instances)))
               nil]

              "instance-pending"
              [(if (contains? (:report-definitions db) (:id msg))
                 (update-in db [:report-definitions (:id msg) :instances]
                            (fn [instances]
                              (conj (vec (remove #(= (:key msg) (:key %)) instances))
                                    (transform-instance {:key         (:key msg)
                                                         :creating-by (:user msg)
                                                         :progress    0}))))
                 db)
               nil]

              "instance-progress"
              [(if (contains? (:report-definitions db) (:id msg))
                 (update-in db [:report-definitions (:id msg) :instances]
                            (fn [instances]
                              (map (fn [instance]
                                     (if (= (:key msg) (:key instance))
                                       (assoc instance
                                         :progress    (:progress msg)
                                         :creating-by (:user msg))
                                       instance))
                                   instances)))
                 db)
               nil]

              "download-progress"
              [(if (contains? (:report-definitions db) (:id msg))
                 (update-in db [:report-definitions (:id msg) :instances]
                            (fn [instances]
                              (map (fn [instance]
                                     (if (= (:key msg) (:key instance))
                                       (assoc instance
                                         :download-progress (:progress msg))
                                       instance))
                                   instances)))
                 db)
               nil]

              "instance-created"
              [db
               (when (contains? (:report-definitions db) (:id msg))
                 [[:reports/refresh-report-instances (get-in db [:report-definitions (:id msg)])]])]

              "download-created"
              [db
               (when (contains? (:report-definitions db) (:id msg))
                 [[:reports/refresh-report-instances (get-in db [:report-definitions (:id msg)])]])]

              ;; Default case
              [db nil]))]
      (cond-> {:db updated-db}
              dispatch (assoc :dispatch-n dispatch)))))

(re-frame/reg-event-fx
  :reports/init-ws
  [common-interceptors]
  (fn [{:keys [db]} _]
    (log/info :reports/init-ws "Reports subscribing to collaboration scopes")
    ;; Websocket is already initialized centrally, just subscribe to reports scopes
    {:db (assoc-in db [:options :collab-custom-events]
                   {"rd-destroyed" [[:reports.ws/message]]
                    "rd-created" [[:reports.ws/message]]
                    "instance-destroyed" [[:reports.ws/message]]
                    "instance-pending" [[:reports.ws/message]]
                    "instance-progress" [[:reports.ws/message]]
                    "download-progress" [[:reports.ws/message]]
                    "instance-created" [[:reports.ws/message]]
                    "download-created" [[:reports.ws/message]]})
     :dispatch-n [[::events/collab-subscribe-scopes scopes]
                  [::events/request-presence [(:current-project-id db)]]]}))

(re-frame/reg-event-fx
 :reports/initialize
 [common-interceptors]
 (fn [{:keys [db]} [options]]
   (log/info :reports/initialize "Initializing internal structure..." db options)
   {:db         (-> (merge {:view               :report-instances
                            :current-rd         {}
                            :page               1
                            :expanded-instances #{}}
                           db
                           options)
                    (assoc :loading? true))
    :dispatch-n [[:reports/init-ws]
                 [:reports/retrieve-users]
                 [:reports/set-default-filter-fields options]
                 [:reports/set-page 1]]}))

(re-frame/reg-event-fx
 :reports/cleanup
 [common-interceptors]
 (fn [_ _]
   (log/info :reports/cleanup "Reports unsubscribing from ws")
   {:fx [[:dispatch [::events/collab-unsubscribe-scopes scopes]]]}))

(re-frame/reg-event-fx
 :reports/retrieve-users
 [common-interceptors]
 (fn [{:keys [db]} _]
   (log/info :reports/retrieve-users)
   {:http-xhrio [{:method          :get
                  :uri             (:users-uri db)
                  :timeout         request-timeout
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:reports/users-received]
                  :on-failure      [:reports/bad-http-result]}]}))

(re-frame/reg-event-fx
 :reports/set-page
 [common-interceptors]
 (fn [{:keys [db]} [page]]
   (log/info :reports/set-page page)
   {:db         (assoc db :loading? true :page page)
    :http-xhrio {:method          :get
                 :uri             (:index-uri db)
                 :params          {:page page}
                 :timeout         request-timeout
                 :vec-strategy    :rails
                 :format          (ajax/url-request-format :rails)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/reports-received :set-page]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-db
 :reports/set-view
 [common-interceptors]
 (fn [db [view]]
   (log/info :reports/set-view view)
   (assoc db :view view)))

(re-frame/reg-event-db
 :reports/toggle-expand-instance
 [common-interceptors]
 (fn [db [k]]
   (log/info :reports/toggle-expand-instance)
   (update db :expanded-instances
           (fn [expanded]
             (if (contains? expanded k)
               (disj expanded k)
               (conj expanded k))))))

(re-frame/reg-event-fx
 :reports/close-modal
 [common-interceptors]
 (fn [{:keys [db]} _]
   (log/info :reports/close-modal)
   {:db       (-> db
                  (assoc-in [:modal-data :show?] false)
                  (dissoc :export))
    :dispatch-n [[:reports/error-msg-clean]
                 [:reports/modal-data-reset]
                 ;; remove query-params from url
                 [::rr/push-state (get-in (:current-route db) [:data :name]) nil nil]]}))

(re-frame/reg-event-db
 :reports/open-confirmation-dialog
 [common-interceptors]
 (fn [db [payload]]
   (log/info :reports/open-confirmation-dialog)
   (assoc db :confirmation-dialog {:show?   true
                                   :size    :small
                                   :payload payload})))

(re-frame/reg-event-db
 :reports/close-confirmation-dialog
 [common-interceptors]
 (fn [db _]
   (log/info :reports/close-confirmation-fialog)
   (assoc-in db [:confirmation-dialog :show?] false)))

(re-frame/reg-event-db
 :reports/new-export
 [common-interceptors]
 (fn [db params]
   (let [param-map (if (map? params) params (first params))
         {:keys [entity cv cv-name fields]} param-map]
     (log/info :reports/new-export entity cv cv-name)
     (let [cv         (js/parseInt cv)
           ;; Parse fields from comma-separated string, ensure "id" is included
           fields     (if (and fields (seq fields))
                        (let [field-vec (vec (.split fields ","))]
                          (if (some #(= "id" %) field-vec)
                            field-vec
                            (cons "id" field-vec)))
                        ["id"])
           fields-map (ru/fields-map (get-in db [:report-fields (keyword entity)]))
           charts     (ru/chart-fields fields-map fields)]
       (-> db
           (assoc :current-rd {:name                 (str entity " export (" cv-name ")")
                               :entity               entity
                               :filter-by            cv
                               :type                 "tabular"
                               :schedule             {:type "manual"}
                               :fields               fields
                               :charts               charts
                               :notify               [(get-in db [:me :id])]
                               :include-description  true
                               :include-fields       true
                               :include-steps        true
                               :include-traceability true
                               :include-history      true
                               :include-comments     true
                               :screen               :create-screen})
           (assoc :modal-data {:show?  true
                               :size   modal-size
                               :dirty? true}))))))

(re-frame/reg-event-db
 :reports/new-report-definition
 [common-interceptors]
 (fn [db _]
   (log/info :reports/new-report-definition)
   (let [default-fields          (ru/default-fields (get-in db [:default-filter-fields :Issue]))
         fields-map              (ru/fields-map (get-in db [:report-fields :Issue]))
         default-charts          (ru/chart-fields fields-map default-fields)]
     (-> db
         (assoc :current-rd {:name                      ""
                             :entity                    "Issue"
                             :filter-by                 nil
                             :run-filter-by             nil
                             :run-filter-by-criteria    nil
                             :type                      "tabular"
                             :schedule                  {:type "manual"}
                             :fields                    default-fields
                             :charts                    default-charts
                             :notify                    [(get-in db [:me :id])]
                             :include-description       true
                             :include-fields            true
                             :include-steps             true
                             :include-traceability      true
                             :include-history           true
                             :include-comments          true})
         (assoc :modal-data {:show? true
                             :size  modal-size
                             :def-fields?  true
                             :def-charts?  true})))))

(re-frame/reg-event-db
  :reports/testset-instances-report
  [common-interceptors]
  (fn [db _]
      (log/info :reports/testset-instances-report)
      (let [default-fields          (ru/default-fields (get-in db [:default-filter-fields :Instance]))
            fields-map              (ru/fields-map (get-in db [:report-fields :Instance]))
            default-charts          (ru/chart-fields fields-map default-fields)
            suite-id (:testset-id-for-instances-filter db)
            report-type (:report-type-name db)]
           (-> db
               (assoc :current-rd {:name                      ""
                                   :entity                    "Instance"
                                   :filter-by                 nil
                                   :run-filter-by             nil
                                   :run-filter-by-criteria    nil
                                   :generate-by               "by_testset_id"
                                   :testset-id                suite-id
                                   :type                      report-type
                                   :schedule                  {:type "manual"}
                                   :fields                    default-fields
                                   :charts                    default-charts
                                   :notify                    [(get-in db [:me :id])]
                                   :include-description       true
                                   :include-fields            true
                                   :include-steps             true
                                   :include-traceability      true
                                   :include-history           true
                                   :include-comments          true
                                   :screen                    :create-screen})
               (assoc :modal-data {:show? true
                                   :size  modal-size
                                   :def-fields?  true
                                   :def-charts?  true})))))

(re-frame/reg-event-db
 :reports/edit-report-definition
 [common-interceptors]
 (fn [db [rd]]
   (log/info :reports/edit-report-definition rd)
   (-> db
       (assoc :current-rd
              (-> rd
                  (update :fields (fn [fields]
                                    (if fields
                                      fields
                                      (->> (get-in db [:report-fields (keyword (:entity rd))])
                                           (map :field)))))
                  (update :charts (fn [charts]
                                    (if charts
                                      charts
                                      (->> (get-in db [:report-fields (keyword (:entity rd))])
                                           (filter ru/chartable-field?)
                                           (map :field)))))))
       (assoc :modal-data {:show? true
                           :size modal-size
                           :def-fields? false
                           :def-charts? false}))))

(re-frame/reg-event-db
 :reports/clone-report-definition
 [common-interceptors]
 (fn [db [rd]]
   (.track js/mixpanel "Clone Report")
   (log/info :reports/clone-report-definition rd)
   (-> db
       (assoc :current-rd
              (-> rd
                  (dissoc :id)
                  (update :name #(str "Clone of " %))
                  (update :fields (fn [fields]
                                    (if fields
                                      fields
                                      (->> (get-in db [:report-fields (keyword (:entity rd))])
                                           (map :field)))))
                  (update :charts (fn [charts]
                                    (if charts
                                      charts
                                      (->> (get-in db [:report-fields (keyword (:entity rd))])
                                           (filter ru/chartable-field?)
                                           (map :field)))))))
       (assoc :modal-data {:show?  true
                           :size   modal-size
                           :dirty? true
                           :def-fields? false
                           :def-charts? false}))))

(re-frame/reg-event-db
 :reports/update-current-rd
 [common-interceptors]
 (fn [db [& {:as kvs}]]
   (log/info :reports/update-current-rd kvs)
   (let [first-key (first (keys kvs))
         params (if (map? first-key) first-key kvs)
         current-entity (get-in db [:current-rd :entity])
         default-fields (get-in db [:default-filter-fields (keyword current-entity)])
         fields-only-change? (and (contains? params :fields)
                                  (seq (:fields params))
                                  (not (contains? params :filter-by))
                                  (not= (set (:fields params)) (set default-fields)))]
     (cond-> db
       :always                         (update :current-rd merge params)
       :always                         (assoc-in [:modal-data :dirty?] true)
       fields-only-change?             (assoc-in [:modal-data :def-fields?] false)
       (contains? params :charts)      (assoc-in [:modal-data :def-charts?] false)
       (not (contains? params :schedule)) (assoc-in [:modal-data :re-run?] true)))))


(re-frame/reg-event-db
 :reports/update-current-rd-notify
 [common-interceptors]
 (fn [db [op us]]
   (log/info :reports/update-current-rd-notify op us)
   (-> db
       (update-in [:current-rd :notify]
                  (fn [users]
                    (case op
                      :add    (vec (concat users us))
                      :remove (vec (remove #(us %) users)))))
       (assoc-in [:modal-data :dirty?] true))))

(re-frame/reg-event-db
 :reports/set-current-rd
 [common-interceptors]
 (fn [db [data]]
   (assoc db :current-rd data)))

(re-frame/reg-event-fx
 :reports/create-report-definition
 [common-interceptors]
 (fn [{:keys [db]} _]
   (log/info :reports/create-report-definition db)
   {:db         (assoc db :report-definition-updating? true)
    :http-xhrio {:method          :post
                 :uri             (:create-uri db)
                 :timeout         request-timeout
                 :params          (:current-rd db)
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-definition-created]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/update-report-definition
 [common-interceptors]
 (fn [{:keys [db]} [uri]]
   (log/info :reports/update-report-definition uri (:current-rd db))
   {:db         (assoc db :report-definition-updating? true)
    :http-xhrio {:method          :put
                 :uri             uri
                 :timeout         request-timeout
                 :params          (merge (:current-rd db) (:modal-data db))
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-definition-updated]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/delete-report-definition
 [common-interceptors]
 (fn [{:keys [db]} [rd]]
   (log/info :reports/delete-report-definition rd)
   {:http-xhrio {:method          :delete
                 :uri             (:uri rd)
                 :timeout         request-timeout
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-definition-deleted rd]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/reactivate-schedule
 [common-interceptors]
 (fn [{:keys [db]} [rd]]
   (log/info :reports/reactivate-schedule rd)
   {:http-xhrio {:method          :put
                  :uri             (str (:uri rd) "/reactivate")
                  :timeout         request-timeout
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [:reports/report-definition-reactivated]
                  :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-db
 :reports/report-definition-reactivated
 [common-interceptors]
 (fn [db [rd]]
   (log/info :reports/report-definition-reactivated rd)
   (let [transformed (-> rd
                         (update :instances #(map transform-instance %))
                         (assoc :filter-name (filter->filter-name (:cvs db) rd)))]
     (-> db
         (assoc-in [:report-definitions (:id rd)] transformed)
         (update :current-rd merge (select-keys rd [:schedule_end_date :schedule_expired]))))))

(re-frame/reg-event-fx
 :reports/create-report-instance
 [common-interceptors]
 (fn [{:keys [db]} [rd]]
   (log/info :reports/create-report-instance rd)
   (.track js/mixpanel "Re-run Create Report Instance")
   {:http-xhrio {:method          :post
                 :uri             (:instance-uri rd)
                 :timeout         request-timeout
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-instance-pending rd]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/recreate-report-instance
 [common-interceptors]
 (fn [{:keys [db]} [rd key]]
   (.track js/mixpanel "Re-run Report Instance")
   (log/info :reports/recreate-report-instance rd key)
   {:http-xhrio {:method          :post
                 :uri             (:instance-uri rd)
                 :params          {:key key}
                 :timeout         request-timeout
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-instance-pending rd]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/refresh-report-instances
 [common-interceptors]
 (fn [{:keys [db]} [rd]]
   (log/info :reports/refresh-report-instances rd)
   {:db         (update db :instances-refreshing
                        (fn [current]
                          (set (conj current (:id rd)))))
    :http-xhrio {:method          :get
                 :uri             (:instances-uri rd)
                 :timeout         request-timeout
                 :vec-strategy    :rails
                 :format          (ajax/url-request-format :rails)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-instances-received rd]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/delete-report-instance
 [common-interceptors]
 (fn [{:keys [db]} [rd delete-uri]]
   (log/info :reports/delete-report-instance rd delete-uri)
   {:http-xhrio {:method          :delete
                 :uri             delete-uri
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/report-instance-deleted rd]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-fx
 :reports/create-download
 [common-interceptors]
 (fn [{:keys [db]} [rd instance format]]
   (log/info :reports/create-download rd instance format)
   {:http-xhrio {:method          :post
                 :uri             (:download-uri rd)
                 :params          {:kind     format
                                   :instance instance}
                 :timeout         request-timeout
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [:reports/download-created rd instance format]
                 :on-failure      [:reports/bad-http-result]}}))

(re-frame/reg-event-db
 :reports/users-received
 [common-interceptors]
 (fn [db [users]]
   (log/info :reports/users-received users)
   (assoc db :users users)))

(re-frame/reg-event-db
 :reports/reports-received
 [common-interceptors]
 (fn [db [origin reports]]
   (log/info :reports/reports-received origin reports)
   ;; we shouldn't screw up with pagination!
   (if (and (= :refresh origin) (not= (:page db) (:page reports)))
     db
     (-> db
         (dissoc :loading? :error)
         (assoc :report-definitions-order (map :id (:results reports)))
         (assoc :report-definitions
                (->> (:results reports)
                     (map (fn [rd]
                            [(:id rd)
                             (-> rd
                                 (update :instances #(map transform-instance %))
                                 (assoc :filter-name (filter->filter-name (:cvs db) rd)))]))
                     (into {})))
         (merge (select-keys reports [:page :per-page :total]))))))

(re-frame/reg-event-db
 :reports/report-instance-pending
 [common-interceptors]
 (fn [db [rd instance]]
   (log/info :report-instance-pending rd instance)
   (update-in db [:report-definitions (:id rd) :instances]
              (fn [instances]
                (conj (vec (remove #(= (:key instance) (:key %)) instances))
                      (transform-instance (assoc instance :creating-by (:me db) :progress 0)))))))

(re-frame/reg-event-db
 :reports/download-created
 [common-interceptors]
 (fn [db [rd instance format]]
   (log/info :reports/download-created rd instance format)
   (update-in db [:report-definitions (:id rd) :instances]
              (fn [instances]
                ;; find the instance
                ;; update the appropriate format
                (map (fn [i]
                       (if (= (:key i) (:key instance))
                         (update i :files assoc format {:label "pending..."
                                                        :url   :pending})
                         i))
                     instances)))))

(re-frame/reg-event-fx
 :reports/report-instance-deleted
 [common-interceptors]
 (fn [{:keys [db]} [rd instance]]
   (let [instances (->> (get-in db [:report-definitions (:id rd) :instances])
                        (remove #(= (:key instance) (:key %)))
                        vec)]
     (cond-> {:db (assoc-in db [:report-definitions (:id rd) :instances] instances)}
       (= 1 (count instances)) (update-in [:db :expanded-instances] disj (:id rd))
       :always (update :db dissoc :confirmation-dialog)))))

(re-frame/reg-event-db
 :reports/report-instances-received
 [common-interceptors]
 (fn [db [rd instances]]
   (log/info :reports/report-instances-received rd instances)
   (let [instances         (map transform-instance instances)
         iim               (into {} (map #(vector (:key %) %) instances))
         pending-instances (difference
                            (->> (get-in db [:report-definitions (:id rd) :instances])
                                 (filter #(nil? (:timestamp %)))
                                 (map :key)
                                 set)
                            (->> instances
                                 (filter (fn [instance]
                                           (and (:data_path instance)
                                                (:meta_path instance))))
                                 (map :key)
                                 set))
         pending-downloads (->> (get-in db [:report-definitions (:id rd) :instances])
                                (filter (fn [instance]
                                          (let [pending-formats (->> (:files instance)
                                                                     (filter (fn [[_ {:keys [url]}]]
                                                                               (= :pending url)))
                                                                     (map first)
                                                                     set)
                                                ready-formats   (->> (:files (get iim (:key instance)))
                                                                     (filter (fn [[_ {:keys [url]}]]
                                                                               (string? url)))
                                                                     (map first)
                                                                     set)]
                                            (seq (difference pending-formats ready-formats)))))
                                (map :key)
                                set)
         still-pending?    (boolean (or (seq pending-instances) (seq pending-downloads)))]
     (log/info :reports/report-instances-received "still pending"
               pending-instances pending-downloads still-pending?)
     (-> db
         (update :instances-refreshing (fn [ids]
                                         (if still-pending?
                                           ids
                                           (disj ids (:id rd)))))
         (update-in [:report-definitions (:id rd) :instances]
                    (fn [current-instances]
                      (let [im (->> current-instances
                                    (map #(vector (:key %) %))
                                    (into {}))]
                        (->> instances
                             (map (fn [instance]
                                    (if (contains? pending-downloads (:key instance))
                                      (get im (:key instance))
                                      instance)))
                             (concat (map #(hash-map :key %) pending-instances))
                             vec))))))))

(re-frame/reg-event-fx
 :reports/report-definition-created
 [common-interceptors]
 (fn [{:keys [db]} [result]]
   (log/info :reports/report-definition-created result)
   {:db (-> db
            (dissoc :report-definition-updating? :error :current-rd :export)
            (assoc-in [:report-definitions (:id result)]
                      (-> result
                          (assoc :filter-name (filter->filter-name (:cvs db) result))
                          (update :instances
                                  (fn [instances]
                                    (map transform-instance instances)))))
            (update :report-definitions-order conj (:id result))
            (assoc-in [:modal-data :show?] false))
    :dispatch (let [current-route (:current-route db)]
              	;; remove query-params from url
                [::rr/push-state (get-in current-route [:data :name]) nil nil])}))

(re-frame/reg-event-db
 :reports/report-definition-updated
 [common-interceptors]
 (fn [db [result]]
   (log/info :reports/report-definition-updated result)
   (-> db
       (dissoc :report-definition-updating? :error :current-rd)
       (assoc-in [:report-definitions (:id result)]
                 (update result :instances
                         (fn [instances]
                           (map transform-instance instances))))
       (assoc-in [:modal-data :show?] false))))

(re-frame/reg-event-db
 :reports/report-definition-deleted
 [common-interceptors]
 (fn [db [result]]
   (log/info :reports/report-definition-deleted result)
   (-> db
       (update :report-definitions dissoc (:id result))
       (update :report-definitions-order (fn [rds]
                                           (remove #(= (:id result) %) rds)))
       (dissoc :confirmation-dialog))))

(re-frame/reg-event-db
 :reports/bad-http-result
 [common-interceptors]
 (fn [db [error]]
   (log/info :reports/bad-http-result error)
   (-> db
       (dissoc :loading? :report-definition-updating? :instances-refreshing)
       (assoc :error (:response error)))))


;; (re-frame/reg-event-db
;;  :reports/change-screen
;;  [common-interceptors]
;;  (fn [db [next-screen]]
;;    (assoc db :edit-mode next-screen)))

(re-frame/reg-event-db
 :reports/update-entity
 [common-interceptors]
 (fn [db [val]]
   (let [entity (keyword val)
         default-fields          (get-in db [:default-filter-fields entity])
         fields-map              (ru/fields-map (get-in db [:report-fields entity]))
         default-charts          (ru/chart-fields fields-map default-fields)]
   (-> db
       (assoc-in [:current-rd :fields] default-fields)
       (assoc-in [:current-rd :charts] default-charts)
       (assoc-in [:current-rd :entity] val)
       (assoc-in [:current-rd :filter-by] nil)))))

(re-frame/reg-event-db
 :reports/update-type
 [common-interceptors]
 (fn [db [val]]
   (assoc-in db [:current-rd :type] val)))

(re-frame/reg-event-fx
  :reports/update-fields
  [common-interceptors]
  (fn [{:keys [db]} [entity]]
    (let [type       (get-in db [:current-rd :type])
          fields-key (if (and (= entity "Requirement") (= type "tabular-traceability"))
                       :RequirementTraceability
                       (keyword entity))]
      {:db db
       :dispatch-n [[:reports/update-current-rd
                     {:fields (get-in db [:default-filter-fields fields-key])}]]})))

(re-frame/reg-event-db
 :reports/update-type-name
 [common-interceptors]
 (fn [db [type-name]]
   (assoc db :selected-report-type-name type-name)))

(re-frame/reg-event-db
 :reports/set-selected-choice
 [common-interceptors]
 (fn [db [val]]
   (assoc db :selected-choice val)))

(re-frame/reg-event-db
 :reports/move-details-screen
 [common-interceptors]
 (fn [db [edit-mode]]
   (reset! edit-mode :details-screen)
   db))

(re-frame/reg-event-db
 :reports/error-msg-clean
 [common-interceptors]
 (fn [db [error]]
   (log/info :reports/error-msg-clean error)
   (-> db
       (dissoc :error))))

(re-frame/reg-event-db
 :reports/modal-data-reset
 [common-interceptors]
 (fn [db [_]]
   (log/info :reports/modal-data-reset)
   (-> db
       (assoc-in [:modal-data :def-fields?] true)
       (assoc-in [:modal-data :def-charts?] true))))
